import React, { memo, useState, useEffect, useRef } from "react";
import { ColumnType } from "~/utils/types/common";
import { ArrowLeftIcon, ArrowRightIcon } from "~/assets/icons";

type JobListTableProps<T extends { id: string }> = {
  data: Array<T>;
  columns: Array<ColumnType>;
  page: number;
  totalPages: number;
  onChangePage?: (page: number) => void;
  onRowClick?: (item: T) => void;
  showPagination?: boolean;
  className?: string;
  totalItems?: number;
  itemsPerPage?: number;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
};

const JobListTable = <T extends { id: string }>(props: JobListTableProps<T>) => {
  const {
    data,
    columns,
    onChangePage,
    totalPages,
    page,
    onRowClick,
    showPagination,
    className,
    totalItems = 0,
    itemsPerPage = 10,
    onItemsPerPageChange,
  } = props;

  const [showItemsDropdown, setShowItemsDropdown] = useState(false);
  const itemsPerPageOptions = [10, 20, 50, 100];
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showItemsDropdown) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowItemsDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showItemsDropdown]);

  const handleChangePage = (event: unknown, newPage: number) => {
    if (onChangePage) {
      onChangePage(newPage);
    }
  };

  const handleRowClick = (item: T) => {
    if (onRowClick) {
      onRowClick(item);
    }
  };

  const handleItemsPerPageChange = (value: number) => {
    if (onItemsPerPageChange) {
      onItemsPerPageChange(value);
    }
    setShowItemsDropdown(false);
  };

  // Calculate which page numbers to show
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxButtons = 5;
    
    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 3) {
        pages.push(1, 2, 3, "...", totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", page - 1, page, page + 1, "...", totalPages);
      }
    }
    
    return pages.filter((p, idx, arr) => !(p === arr[idx - 1]));
  };

  return (
    <div className={`w-full h-full p-5 bg-[#0E264C] rounded-xl flex flex-col ${className || ""}`}>
      {/* Table Container with fixed header and scrollable body */}
      <div className="w-full flex-1 flex flex-col min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar">
        {/* Fixed Header */}
        <div className="sticky top-0 z-10 bg-[#0E264C] border-b border-[#111e33] flex-shrink-0">
          <div className="flex items-center h-[54px]">
            {columns.map((column) => (
              <div
                key={column.id as string}
                className="px-4 text-sm font-semibold text-[#f9fbfe] truncate"
                style={{
                  width: column.width,
                  minWidth: column.minWidth,
                  flex: column.width ? `0 0 ${column.width}` : "1",
                }}
              >
                {column.labelHeader ?? column.id}
              </div>
            ))}
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="w-full">
          {data.length > 0 ? (
            <>
              {data.map((item: T) => (
                <div
                  key={item.id}
                  className={`flex items-center min-h-[76px] border-b border-[#111e33] last:border-b-0 ${
                    onRowClick
                      ? "cursor-pointer transition-all duration-200 hover:bg-[#0a1b34] hover:scale-[1.02] hover:shadow-[0_2px_8px_rgba(32,79,150,0.15)] active:bg-[#071224] active:scale-[0.98] active:shadow-none"
                      : ""
                  }`}
                  onClick={() => handleRowClick(item)}
                >
                  {columns.map((column, index) => (
                    <div
                      key={column.id as string}
                      className="px-4 text-sm font-normal text-[#f9fbfe] flex items-center truncate"
                      style={{
                        width: column.width,
                        minWidth: column.minWidth,
                        flex: column.width ? `0 0 ${column.width}` : "1",
                      }}
                    >
                      {column.renderCell?.(item, index)}
                    </div>
                  ))}
                </div>
              ))}
            </>
          ) : (
            <div className="w-full flex items-center justify-center py-5">
              <h3 className="text-sm font-normal text-[#949699]">No items</h3>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {showPagination && (
        <div className="flex justify-between items-center mt-5 pt-4 border-t border-[#111e33]">
          {/* Left side: Show result dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-normal text-[#789EB4]">Show result</span>
            <div className="relative" ref={dropdownRef}>
              <button
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#0c192d] border border-[#111e33] text-sm font-normal text-[#789EB4] cursor-pointer hover:border-[#2d80fc] transition-colors"
                onClick={() => setShowItemsDropdown(!showItemsDropdown)}
              >
                <span>{itemsPerPage}</span>
                <svg width="6" height="5" viewBox="0 0 9 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4.243 5.656L0 1.414L1.415 0L4.243 2.828L7.071 0L8.486 1.414L4.243 5.656Z" fill="#789EB4"/>
                </svg>
              </button>
              {showItemsDropdown && (
                <div className="absolute top-[-300%] left-0 mt-1 bg-[#030e1f] border border-[#1f2937] rounded-lg shadow-lg z-50 min-w-[80px]">
                  {itemsPerPageOptions.map((option) => (
                    <button
                      key={option}
                      className={`w-full px-3 py-2 text-left text-sm font-normal text-white hover:bg-[#1f2937] transition-colors ${
                        itemsPerPage === option ? "bg-[#1f2937] text-[#789EB4]" : ""
                      }`}
                      onClick={() => handleItemsPerPageChange(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <span className="text-sm font-normal text-[#789EB4]">out of</span>
            <span className="text-sm font-normal text-[#079EF5]">{totalItems}</span>
          </div>

          {/* Right side: Page navigation */}
          <div className="flex items-center gap-2">
            <button
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-normal transition-colors ${
                page === 1 || page === 0
                  ? "text-[#949699] cursor-not-allowed opacity-50"
                  : "text-[#949699] hover:text-[#f9fbfe] hover:bg-[#0c192d] cursor-pointer text-[#789EB4]"
              }`}
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1 || page === 0}
            >
              <ArrowLeftIcon className="w-4 h-4 text-[#789EB4]" />
              <span>Previous</span>
            </button>
            
            <div className="flex items-center gap-1">
              {getPageNumbers().map((pageNum, index) => {
                if (pageNum === "...") {
                  return (
                    <span key={`ellipsis-${index}`} className="px-2 text-sm text-[#789EB4]">
                      ...
                    </span>
                  );
                }
                const pageNumber = Number(pageNum);
                const isActive = page === pageNumber;
                return (
                  <button
                    key={pageNumber}
                    className={`min-w-[32px] h-8 px-2 rounded-lg text-sm font-normal transition-colors ${
                      isActive
                        ? "bg-[#2d80fc] text-white"
                        : "text-[#f9fbfe] hover:bg-[#0c192d] text-[#789EB4]"
                    }`}
                    onClick={() => handleChangePage(null, pageNumber)}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>

            <button
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-normal transition-colors ${
                page === totalPages
                  ? "text-[#949699] cursor-not-allowed opacity-50"
                  : "text-[#949699] hover:text-[#f9fbfe] hover:bg-[#0c192d] cursor-pointer text-[#789EB4]"
              }`}
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page === totalPages}
            >
              <span className="text-[#789EB4]">Next</span>
              <ArrowRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.3);
        }
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.093) transparent;
        }
      `}</style>
    </div>
  );
};

export default memo(JobListTable) as typeof JobListTable;

